/**
 * GSTN's auto-calculated GSTR-3B figures on the 3B screen (D-10, FR-E35): shown beside
 * ProGST's before the set-off, each difference in the service's sentence. ProGST's figures are
 * never replaced by them — the card says so.
 *
 * Its own file, as `Gstr3bLedgers.tsx` is, so the lifecycle's edits to `Gstr3b.tsx` stay
 * mergeable.
 */

import { Alert, Button } from '@fiveox/ui'
import { useCallback, useEffect, useState } from 'react'

import { ApiError, type Gstr3bAutocalc as Comparison, filing } from '../lib/api'
import { refusalWords } from '../lib/filingLane'
import { autocalcLines, canFetch, differing, figureWords } from '../lib/gstr3bAutocalc'

export function Gstr3bAutocalc({
  periodId,
  periodState,
  mayUpdate,
}: {
  periodId: string
  periodState: string | undefined
  mayUpdate: boolean
}) {
  const [comparison, setComparison] = useState<Comparison | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      setComparison((await filing.gstr3bAutocalc(periodId)).data)
    } catch {
      setComparison(null)
    }
  }, [periodId])

  useEffect(() => {
    setError(null)
    void load()
  }, [load])

  const fetchNow = async () => {
    setBusy(true)
    setError(null)
    try {
      setComparison((await filing.fetchGstr3bAutocalc(periodId)).data)
    } catch (err) {
      setError(err instanceof ApiError ? refusalWords(err) : 'GSTN’s figures could not be fetched.')
    } finally {
      setBusy(false)
    }
  }

  if (!comparison) return null
  const differ = differing(comparison)

  return (
    <Alert tone={differ.length ? 'warning' : 'info'} className="mb-5">
      <p className="gst-panel-h">
        GSTN’s auto-calculated figures
        <span className={`gst-tag ${comparison.fetched && !differ.length ? 'is-clear' : ''}`}>
          {!comparison.fetched
            ? 'not fetched'
            : differ.length
              ? `${differ.length} difference${differ.length === 1 ? '' : 's'}`
              : 'agree'}
        </span>
      </p>
      {autocalcLines(comparison).map((line) => (
        <p key={line} className="text-sm">
          {line}
        </p>
      ))}
      {differ.length > 0 && (
        <div className="gst-table-wrap mt-2">
          <table className="gst-table">
            <thead>
              <tr>
                <th className="is-text">Cell</th>
                <th className="is-text">Figure</th>
                <th>ProGST</th>
                <th>GSTN</th>
              </tr>
            </thead>
            <tbody>
              {differ.map((f) => (
                <tr key={`${f.cell}|${f.part}`} title={f.sentence ?? undefined}>
                  <td className="is-text is-sec">{f.cell}</td>
                  <td className="is-text">{f.part}</td>
                  <td>{figureWords(f.ours)}</td>
                  <td>{figureWords(f.gstn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {differ.length > 0 && (
        <ul className="gst-panel-list mt-2">
          {comparison.differences.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      )}
      {error && <p className="gst-meta mt-1">{error}</p>}
      {canFetch(periodState, mayUpdate) && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2"
          disabled={busy}
          onClick={() => void fetchNow()}
        >
          {comparison.fetched ? 'Fetch GSTN’s figures again' : 'Fetch GSTN’s figures'}
        </Button>
      )}
    </Alert>
  )
}
